import React from 'react';
import { colors } from '../theme/tokens';

// Renders the top-N match cards: thumbnail, profile code, name, match% bar,
// and Confirm / Wrong buttons that send feedback to the server.
const ProfileMatchResults = ({ results, onConfirm, onWrong, onNoneOfThese, token, apiUrl }) => {
  if (!results || results.length === 0) return null;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span style={styles.headerText}>Top matches</span>
        <button style={styles.noneBtn} onClick={onNoneOfThese}>
          🚫 None of these
        </button>
      </div>

      <div style={styles.grid}>
        {results.map((r, idx) => (
          <ResultCard
            key={r.profileId || r.profileCode || idx}
            rank={idx + 1}
            result={r}
            onConfirm={() => onConfirm && onConfirm(r)}
            onWrong={() => onWrong && onWrong(r)}
            token={token}
            apiUrl={apiUrl}
          />
        ))}
      </div>
    </div>
  );
};

const ResultCard = ({ rank, result, onConfirm, onWrong, token, apiUrl }) => {
  const pct = Math.max(0, Math.min(100, Math.round(result.matchPercent || 0)));
  const barColor = pct >= 80 ? '#2fb65c' : pct >= 60 ? '#f39c12' : '#999';

  const imgSrc =
    result.profileId && result.bestReferenceId
      ? `${apiUrl}/api/profiles/${result.profileId}/references/${result.bestReferenceId}/image`
      : null;

  return (
    <div style={styles.card}>
      <div style={styles.rankBadge}>#{rank}</div>
      <div style={styles.thumb}>
        {imgSrc ? (
          <AuthImage src={imgSrc} alt={result.profileCode} token={token} />
        ) : (
          <div style={styles.thumbPlaceholder}>No image</div>
        )}
      </div>
      <div style={styles.info}>
        <div style={styles.code}>{result.profileCode}</div>
        <div style={styles.name}>{result.name}</div>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width: `${pct}%`, background: barColor }} />
        </div>
        <div style={styles.pctRow}>
          <span style={{ color: barColor, fontWeight: 700 }}>{pct}% match</span>
          {result.category && <span style={styles.category}>{result.category}</span>}
        </div>
        {(result.bestRotation || result.bestMirror) ? (
          <div style={styles.orientHint} title="Best orientation found for this match">
            {result.bestRotation ? `↻ ${result.bestRotation}°` : ''}
            {result.bestMirror ? ' · 🪞 mirrored' : ''}
          </div>
        ) : null}
        <div style={styles.actions}>
          <button style={styles.confirmBtn} onClick={onConfirm}>✓ This one</button>
          <button style={styles.wrongBtn} onClick={onWrong}>✕ Wrong</button>
        </div>
      </div>
    </div>
  );
};

// Loads a protected image (Bearer token) into a blob URL so we can use <img>.
class AuthImage extends React.Component {
  state = { blobUrl: null, err: null };
  async componentDidMount() {
    try {
      const res = await fetch(this.props.src, {
        headers: { Authorization: `Bearer ${this.props.token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      this.setState({ blobUrl: url });
    } catch (e) {
      this.setState({ err: e.message });
    }
  }
  componentWillUnmount() {
    if (this.state.blobUrl) URL.revokeObjectURL(this.state.blobUrl);
  }
  render() {
    if (this.state.err) {
      return <div style={styles.thumbPlaceholder}>No image</div>;
    }
    if (!this.state.blobUrl) {
      return <div style={styles.thumbPlaceholder}>…</div>;
    }
    return <img src={this.state.blobUrl} alt={this.props.alt} style={styles.thumbImg} />;
  }
}

const styles = {
  wrap: { marginTop: '16px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  headerText: { fontSize: '16px', fontWeight: 700, color: colors.text },
  noneBtn: {
    padding: '6px 12px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 600,
    color: colors.text
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px'
  },
  card: {
    background: colors.surface,
    borderRadius: '12px',
    padding: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    position: 'relative',
    display: 'flex',
    gap: '12px'
  },
  rankBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    background: '#1E1E1E',
    color: 'white',
    fontSize: '11px',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '6px'
  },
  thumb: {
    width: '90px',
    height: '90px',
    flexShrink: 0,
    background: colors.surfaceDeep,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'contain' },
  thumbPlaceholder: { color: colors.textMuted, fontSize: '12px', textAlign: 'center', padding: '8px' },
  info: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  code: { fontSize: '14px', fontWeight: 700, color: colors.parGreen },
  name: { fontSize: '13px', color: colors.text, marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  barTrack: {
    height: '8px',
    background: colors.surface2,
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '4px'
  },
  barFill: { height: '100%', transition: 'width 0.3s' },
  pctRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    marginBottom: '8px'
  },
  category: { color: colors.textMuted },
  orientHint: { fontSize: '11px', color: '#7c4dff', marginBottom: '4px', fontWeight: 600 },
  actions: { display: 'flex', gap: '6px', marginTop: 'auto' },
  confirmBtn: {
    flex: 1,
    padding: '6px 10px',
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  wrongBtn: {
    flex: 1,
    padding: '6px 10px',
    background: colors.surface,
    color: colors.textMuted,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default ProfileMatchResults;
